/**
 * Autotile Rule Editor (_docs/tilemap/07 roadmap). Lets the artist define
 * one or more named "terrains" for the active layer's tileset and, for each
 * of the 16 N/E/S/W neighbor patterns, assign one or more candidate atlas
 * tiles. Painting with the Autotile tool (toolbar) then picks the right
 * variant automatically as the surrounding terrain changes — see
 * `model/autotile.ts` for the algorithm this rule table drives.
 *
 * Deliberately NOT a fixed sheet-layout convention (RPG Maker/Godot/Tiled
 * each use a different one) — the artist assigns tiles to patterns
 * themselves, so it works with whatever a tileset actually contains.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTilemapStore, tilemapStore } from '../store/tilemapStore';
import { tilemapClient } from '../api/tilemapClient';
import { TilesetImage } from '../surface/tileset';
import { BIT_E, BIT_N, BIT_S, BIT_W } from '../model/autotile';
import type { AutotileTerrain, Tileset } from '../model/types';

const ORANGE = '#F5793B';

export function AutotileRuleEditor({ projectUuid }: { projectUuid: string }) {
    const doc = useTilemapStore((s) => s.doc);
    const activeLayerUuid = useTilemapStore((s) => s.activeLayerUuid);
    const activeTerrain = useTilemapStore((s) => s.activeTerrain);
    const [editingPattern, setEditingPattern] = useState<number | null>(null);

    const tileset = useMemo<Tileset | null>(() => {
        if (!doc) return null;
        const layer = doc.layers.find((l) => l.uuid === activeLayerUuid);
        return doc.tilesets.find((t) => t.uuid === layer?.tileset_uuid) ?? null;
    }, [doc, activeLayerUuid]);

    if (!doc) return null;
    if (!tileset) {
        return <div className="p-3 text-secondary small">The active layer has no tileset.</div>;
    }

    const terrains = tileset.autotile_rules ?? {};
    const terrainNames = Object.keys(terrains);
    const selected = activeTerrain ? terrains[activeTerrain] : undefined;

    const saveRules = async (next: Record<string, AutotileTerrain>) => {
        await tilemapClient.updateAutotileRules(projectUuid, tileset.uuid, next);
        await tilemapStore.getState().reload();
    };

    const addTerrain = async () => {
        const name = prompt('Terrain name (e.g. grass, water, path)')?.trim();
        if (!name || terrains[name]) return;
        await saveRules({ ...terrains, [name]: { rules: {} } });
        tilemapStore.getState().setActiveTerrain(name);
    };

    const renameTerrain = async (oldName: string) => {
        const nextName = prompt('Rename terrain', oldName)?.trim();
        if (!nextName || nextName === oldName || terrains[nextName]) return;
        const rest = { ...terrains };
        const data = rest[oldName];
        delete rest[oldName];
        await saveRules({ ...rest, [nextName]: data });
        if (activeTerrain === oldName) tilemapStore.getState().setActiveTerrain(nextName);
    };

    const deleteTerrain = async (name: string) => {
        if (!confirm(`Delete terrain "${name}" and its rules? Already-painted tiles are unaffected.`)) return;
        const rest = { ...terrains };
        delete rest[name];
        await saveRules(rest);
        if (activeTerrain === name) tilemapStore.getState().setActiveTerrain(null);
    };

    const toggleCandidate = async (pattern: number, atlasIndex: number) => {
        if (!activeTerrain || !selected) return;
        const current = selected.rules[String(pattern)] ?? [];
        const next = current.includes(atlasIndex) ? current.filter((c) => c !== atlasIndex) : [...current, atlasIndex];
        await saveRules({ ...terrains, [activeTerrain]: { rules: { ...selected.rules, [String(pattern)]: next } } });
    };

    return (
        <div className="d-flex flex-column h-100">
            <div className="d-flex align-items-center gap-2 p-2 border-bottom">
                <span className="fw-bold small">Autotile Terrains</span>
                <button className="btn btn-sm btn-outline-primary ms-auto" onClick={addTerrain}>
                    <i className="ti ti-plus" /> New
                </button>
            </div>

            <div className="d-flex flex-wrap gap-1 p-2 border-bottom">
                {terrainNames.length === 0 && <span className="text-secondary small">No terrains yet.</span>}
                {terrainNames.map((name) => (
                    <div key={name} className="btn-group btn-group-sm">
                        <button
                            className={`btn ${activeTerrain === name ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={() => { tilemapStore.getState().setActiveTerrain(name); setEditingPattern(null); }}
                        >
                            {name}
                        </button>
                        <button className="btn btn-outline-secondary" title="Rename" onClick={() => renameTerrain(name)}>
                            <i className="ti ti-pencil" />
                        </button>
                        <button className="btn btn-outline-danger" title="Delete" onClick={() => deleteTerrain(name)}>
                            <i className="ti ti-trash" />
                        </button>
                    </div>
                ))}
            </div>

            {activeTerrain && selected ? (
                <div className="overflow-auto flex-fill p-2">
                    <div className="small text-secondary mb-2">
                        Click a pattern, then click atlas tiles to assign them. Multiple tiles per pattern add
                        random visual variety when painting.
                    </div>
                    <div className="row g-2 mb-3">
                        {Array.from({ length: 16 }, (_, pattern) => (
                            <div key={pattern} className="col-3">
                                <button
                                    className={`btn btn-sm w-100 d-flex flex-column align-items-center p-1 ${editingPattern === pattern ? 'btn-primary' : 'btn-outline-secondary'}`}
                                    onClick={() => setEditingPattern(editingPattern === pattern ? null : pattern)}
                                    title={`Pattern ${pattern}`}
                                >
                                    <NeighborDiagram pattern={pattern} />
                                    <span className="small">{(selected.rules[String(pattern)] ?? []).length}</span>
                                </button>
                            </div>
                        ))}
                    </div>

                    {editingPattern !== null && (
                        <div>
                            <div className="small fw-bold mb-1 d-flex align-items-center gap-2">
                                Assign tiles to pattern {editingPattern}
                                <NeighborDiagram pattern={editingPattern} />
                            </div>
                            <AtlasCellPicker
                                tileset={tileset}
                                selected={selected.rules[String(editingPattern)] ?? []}
                                onToggle={(atlasIndex) => toggleCandidate(editingPattern, atlasIndex)}
                            />
                        </div>
                    )}
                </div>
            ) : (
                <div className="p-3 text-secondary small">Select or create a terrain to edit its rules.</div>
            )}
        </div>
    );
}

/** A 3×3 mini-diagram showing which of the N/E/S/W neighbor bits this pattern has set. */
function NeighborDiagram({ pattern }: { pattern: number }) {
    const on = [
        false, !!(pattern & BIT_N), false,
        !!(pattern & BIT_W), true, !!(pattern & BIT_E),
        false, !!(pattern & BIT_S), false,
    ];
    return (
        <div style={{ display: 'inline-grid', gridTemplateColumns: 'repeat(3, 7px)', gridTemplateRows: 'repeat(3, 7px)', gap: 1 }}>
            {on.map((isOn, i) => (
                <div key={i} style={{ width: 7, height: 7, background: isOn ? ORANGE : 'rgba(128,128,128,0.25)' }} />
            ))}
        </div>
    );
}

/** A compact atlas grid supporting multi-select-by-click (for candidate assignment). */
function AtlasCellPicker({ tileset, selected, onToggle }: { tileset: Tileset; selected: number[]; onToggle: (atlasIndex: number) => void }) {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const [img, setImg] = useState<TilesetImage | null>(null);
    const zoom = 2;
    const cols = tileset.columns ?? 1;
    const rows = tileset.tile_count && cols ? Math.ceil(tileset.tile_count / cols) : 1;
    const tw = tileset.tile_width;
    const th = tileset.tile_height;

    useEffect(() => {
        let alive = true;
        TilesetImage.load(tileset).then((t) => alive && setImg(t)).catch(() => setImg(null));
        return () => { alive = false; };
    }, [tileset.uuid, tileset.texture_asset_uuid]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || !img) return;
        canvas.width = cols * tw * zoom;
        canvas.height = rows * th * zoom;
        const ctx = canvas.getContext('2d')!;
        ctx.imageSmoothingEnabled = false;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        for (let r = 0; r < rows; r++) {
            for (let c = 0; c < cols; c++) {
                const idx = r * cols + c;
                if (tileset.tile_count != null && idx >= tileset.tile_count) continue;
                const src = img.tileRectPx(idx);
                ctx.drawImage(img.image, src.x, src.y, src.w, src.h, c * tw * zoom, r * th * zoom, tw * zoom, th * zoom);
                if (selected.includes(idx)) {
                    ctx.strokeStyle = ORANGE;
                    ctx.lineWidth = 2;
                    ctx.strokeRect(c * tw * zoom + 1, r * th * zoom + 1, tw * zoom - 2, th * zoom - 2);
                }
            }
        }
    }, [img, selected, cols, rows, tw, th, tileset.tile_count]);

    return (
        <canvas
            ref={canvasRef}
            style={{ imageRendering: 'pixelated', cursor: 'pointer', maxWidth: '100%', display: 'block' }}
            onClick={(e) => {
                const rect = canvasRef.current!.getBoundingClientRect();
                const c = Math.floor((e.clientX - rect.left) / (tw * zoom));
                const r = Math.floor((e.clientY - rect.top) / (th * zoom));
                onToggle(r * cols + c);
            }}
        />
    );
}
